import React, { useState } from 'react';
import { toast } from 'sonner';
import { Truck } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { formatPrice } from '../utils';

const ShippingView = () => {
const [selectedDept, setSelectedDept] = useState('');
    const [calculatedCosts, setCalculatedCosts] = useState(null);
    const [calculating, setCalculating] = useState(false);

    // Tarifs de base par zone (coût transporteur)
    const ZONE_RATES = {
      "marignane": 0, // Gratuit - on livre nous-même
      "paca": 58.56,
      "lyon": 87.82,
      "paris": 97.93,
      "standard": 77.15,
      "remote": 95.0
    };

    // Zones par département
    const DEPARTMENT_ZONES = {
      "13": "marignane", // Bouches-du-Rhône - gratuit
      "83": "paca", "84": "paca", "06": "paca", "04": "paca", "05": "paca",
      "69": "lyon", "01": "lyon", "38": "lyon", "42": "lyon", "43": "lyon", "07": "lyon", "26": "lyon", "73": "lyon", "74": "lyon",
      "75": "paris", "77": "paris", "78": "paris", "91": "paris", "92": "paris", "93": "paris", "94": "paris", "95": "paris",
    };

    const REMOTE_DEPTS = ["29", "22", "56", "35", "44", "85", "17", "33", "40", "64", "65", "66", "11", "09", "31", "32", "47", "24", "19", "23", "87", "16", "79", "86", "36", "37", "41", "18", "03", "63", "15", "12", "46", "82", "81", "34", "30", "48"];

    // Poids et surfaces des packs
    const PACKS = [
      { id: 'dj_booth', name: 'Pack S - DJ Booth 2m²', surface: 2, weight: 96 },
      { id: 'compact', name: 'Pack M - Compact 4m²', surface: 4, weight: 192 },
      { id: 'standard', name: 'Pack L - Standard 7m²', surface: 7, weight: 338 },
      { id: 'xl', name: 'Pack XL - Premium 15m²', surface: 15, weight: 718 },
    ];

    const getZone = (dept) => {
      if (DEPARTMENT_ZONES[dept]) return DEPARTMENT_ZONES[dept];
      if (REMOTE_DEPTS.includes(dept)) return "remote";
      return "standard";
    };

    const getZoneName = (zone) => {
      const names = {
        "marignane": "Bouches-du-Rhône (13)",
        "paca": "PACA",
        "lyon": "Lyon / Rhône-Alpes",
        "paris": "Paris / IDF",
        "standard": "France standard",
        "remote": "Zone éloignée"
      };
      return names[zone] || zone;
    };

    const calculateCost = (zone, weight) => {
      if (zone === "marignane") return 0;
      const baseRate = ZONE_RATES[zone] || ZONE_RATES.standard;
      // Coût = taux de base × (poids / 100) × 2 (aller-retour) × 1.10 (marge 10%)
      const cost = baseRate * (weight / 100) * 2 * 1.10;
      return Math.round(cost);
    };

    const handleCalculate = () => {
      if (!selectedDept || selectedDept.length < 2) {
        toast.error('Veuillez entrer un code département valide (2 chiffres)');
        return;
      }
      
      const dept = selectedDept.slice(0, 2);
      const zone = getZone(dept);
      
      const costs = PACKS.map(pack => ({
        ...pack,
        cost: calculateCost(zone, pack.weight)
      }));
      
      setCalculatedCosts({
        department: dept,
        zone: zone,
        zoneName: getZoneName(zone),
        packs: costs
      });
    };

    // Tableau des tarifs par défaut pour chaque zone (recalculés avec poids réels)
    // Formule: taux × (poids/100) × 2 × 1.10
    const defaultRates = [
      { zone: 'Bouches-du-Rhône (13)', dj: 0, compact: 0, standard: 0, xl: 0, note: 'Livraison gratuite' },
      { zone: 'PACA', dj: 124, compact: 248, standard: 435, xl: 924, note: 'Aller-retour inclus' },
      { zone: 'Lyon / Rhône-Alpes', dj: 185, compact: 371, standard: 652, xl: 1385, note: 'Aller-retour inclus' },
      { zone: 'Paris / IDF', dj: 207, compact: 413, standard: 727, xl: 1545, note: 'Aller-retour inclus' },
      { zone: 'France standard', dj: 163, compact: 326, standard: 573, xl: 1217, note: 'Aller-retour inclus' },
      { zone: 'Zone éloignée', dj: 200, compact: 401, standard: 705, xl: 1498, note: 'Bretagne, Sud-Ouest...' },
    ];

    return (
      <div className="space-y-6">
        <h2 className="text-xl font-bold text-white">Calcul frais de transport</h2>
        
        {/* Calculateur */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
          <h3 className="font-semibold text-white mb-4">Calculateur par département</h3>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm text-slate-400 block mb-2">Code département</label>
              <Input 
                placeholder="Ex: 75, 13, 29..." 
                className="bg-slate-800 border-slate-700 text-white"
                value={selectedDept}
                onChange={(e) => setSelectedDept(e.target.value.replace(/\D/g, '').slice(0, 2))}
                maxLength={2}
              />
            </div>
            <Button 
              className="bg-[#DFFF00] hover:bg-[#c8e600] text-black px-8"
              onClick={handleCalculate}
            >
              Calculer
            </Button>
          </div>

          {/* Résultat du calcul */}
          {calculatedCosts && (
            <div className="mt-6 p-4 bg-slate-800/50 rounded-lg border border-[#DFFF00]/30">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-slate-400">Département {calculatedCosts.department} →</span>
                <span className="text-[#DFFF00] font-semibold">{calculatedCosts.zoneName}</span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {calculatedCosts.packs.map(pack => (
                  <div key={pack.id} className="bg-slate-900 rounded-lg p-4 text-center">
                    <p className="text-sm text-slate-400 mb-1">{pack.name}</p>
                    <p className="text-xs text-slate-500 mb-2">{pack.surface}m² - {pack.weight}kg</p>
                    <p className="text-2xl font-bold text-[#DFFF00]">
                      {pack.cost === 0 ? 'Gratuit' : `${pack.cost}€`}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Grille tarifaire complète */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
          <div className="p-4 border-b border-slate-800">
            <h3 className="font-semibold text-white">Grille tarifaire (aller-retour)</h3>
            <p className="text-sm text-slate-400">Tarifs calculés : base transporteur × poids × 2 × 1.10</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Zone</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-slate-400">S - DJ Booth 2m²<br/><span className="text-[#DFFF00]">2m² - 96kg</span></th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-slate-400">M - Compact 4m²<br/><span className="text-[#DFFF00]">4m² - 192kg</span></th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-slate-400">L - Standard 7m²<br/><span className="text-[#DFFF00]">7m² - 338kg</span></th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-slate-400">XL - Premium 15m²<br/><span className="text-[#DFFF00]">15m² - 718kg</span></th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-slate-400">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {defaultRates.map((row, i) => (
                  <tr key={i} className={`hover:bg-slate-800/30 ${i === 0 ? 'bg-green-500/10' : ''}`}>
                    <td className="px-4 py-3 text-sm text-white font-medium">{row.zone}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-bold ${row.dj === 0 ? 'text-green-400' : 'text-white'}`}>
                        {row.dj === 0 ? 'Gratuit' : `${row.dj}€`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-bold ${row.compact === 0 ? 'text-green-400' : 'text-white'}`}>
                        {row.compact === 0 ? 'Gratuit' : `${row.compact}€`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-bold ${row.standard === 0 ? 'text-green-400' : 'text-white'}`}>
                        {row.standard === 0 ? 'Gratuit' : `${row.standard}€`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`font-bold ${row.xl === 0 ? 'text-green-400' : 'text-white'}`}>
                        {row.xl === 0 ? 'Gratuit' : `${row.xl}€`}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{row.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Informations */}
        <div className="bg-slate-800/50 rounded-lg p-4 text-sm text-slate-400">
          <p><strong className="text-white">Base de calcul :</strong></p>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Poids incluant flight cases : S - DJ Booth 2m² 96kg, M - Compact 4m² 192kg, L - Standard 7m² 338kg, XL - Premium 15m² 718kg</li>
            <li>Formule : Tarif zone × (Poids / 100) × 2 (A/R) × 1.10 (marge 10%)</li>
            <li>Bouches-du-Rhône (13) : livraison assurée par nos soins</li>
          </ul>
        </div>
      </div>
    );
  

}

export default ShippingView;
